import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../components/Topbar";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import {
  Inbox,
  ArrowLeft,
  Mail,
  Phone,
  Trash2,
  Archive,
  CheckCircle2,
  RefreshCcw,
  Loader2,
  Filter,
  FileText,
  Download,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import ConfirmDialog from "../components/ConfirmDialog";

const STATUS_LABEL = {
  new: "Nuova",
  handled: "Gestita",
  archived: "Archiviata",
};
const STATUS_BADGE = {
  new: "bg-emerald-50 text-emerald-700 border-emerald-200",
  handled: "bg-brand-blue/5 text-brand-blue border-brand-blue/20",
  archived: "bg-brand-bg text-brand-muted border-brand-line",
};

function formatDate(iso) {
  if (!iso) return "—";
  try {
    return new Intl.DateTimeFormat("it-IT", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function DetailDialog({ item, open, onOpenChange, onUpdated, onDeleted }) {
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    setNotes(item?.notes || "");
  }, [item]);

  if (!item) return null;

  const setStatus = async (status) => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/contact-requests/${item.id}`, {
        status,
        notes,
      });
      onUpdated(data);
      toast.success(
        status === "handled"
          ? "Richiesta segnata come gestita"
          : status === "archived"
          ? "Richiesta archiviata"
          : "Richiesta aggiornata"
      );
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const saveNotes = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/admin/contact-requests/${item.id}`, {
        notes,
      });
      onUpdated(data);
      toast.success("Note salvate");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const downloadAttachment = async () => {
    try {
      const res = await api.get(`/admin/contact-requests/${item.id}/attachment`, {
        responseType: "blob",
      });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = item.attachment_filename || "modulo-iscrizione.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const doDelete = async () => {
    try {
      await api.delete(`/admin/contact-requests/${item.id}`);
      onDeleted(item.id);
      toast.success("Richiesta eliminata");
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setConfirmDelete(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          data-testid="contact-detail-dialog"
          className="max-w-2xl rounded-sm bg-white"
        >
          <DialogHeader>
            <DialogTitle className="wordmark text-brand-blue">
              Dettaglio richiesta
            </DialogTitle>
            <DialogDescription>
              Ricevuta il {formatDate(item.created_at)}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-3">
              <div className="bg-brand-bg border border-brand-line rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-brand-muted">
                  Nome
                </div>
                <div className="text-sm font-semibold text-brand-ink">
                  {item.name}
                </div>
              </div>
              <div className="bg-brand-bg border border-brand-line rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-brand-muted">
                  Email
                </div>
                <div className="text-sm font-semibold text-brand-blue break-all">
                  <a href={`mailto:${item.email}`} className="hover:underline">
                    {item.email}
                  </a>
                </div>
              </div>
              <div className="bg-brand-bg border border-brand-line rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-brand-muted">
                  Telefono
                </div>
                <div className="text-sm font-semibold text-brand-ink">
                  {item.phone || "—"}
                </div>
              </div>
              <div className="bg-brand-bg border border-brand-line rounded-sm p-3">
                <div className="text-[10px] uppercase tracking-widest text-brand-muted">
                  Corso di interesse
                </div>
                <div className="text-sm font-semibold text-brand-ink">
                  {item.course_title_snapshot || "Richiesta generica"}
                </div>
              </div>
            </div>

            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-muted mb-1">
                Messaggio
              </div>
              <div
                data-testid="contact-detail-message"
                className="bg-white border border-brand-line rounded-sm p-3 text-sm text-brand-ink whitespace-pre-wrap"
              >
                {item.message}
              </div>
            </div>

            {item.attachment_filename && (
              <div>
                <div className="text-[10px] uppercase tracking-widest text-brand-muted mb-1">
                  Allegato
                </div>
                <button
                  type="button"
                  onClick={downloadAttachment}
                  data-testid="contact-detail-download-attachment"
                  className="w-full flex items-center gap-3 rounded-sm border border-brand-line hover:border-brand-blue/40 hover:bg-brand-blue/5 p-2.5 text-left transition-colors"
                >
                  <span className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-sm bg-brand-blue/10 text-brand-blue">
                    <FileText size={16} />
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block text-sm text-brand-ink truncate">
                      {item.attachment_filename}
                    </span>
                    <span className="block text-[11px] text-brand-muted">
                      PDF
                      {item.attachment_size
                        ? ` · ${Math.round(item.attachment_size / 1024)} KB`
                        : ""}
                    </span>
                  </span>
                  <Download size={16} className="text-brand-muted shrink-0" />
                </button>
              </div>
            )}

            <div>
              <div className="text-[10px] uppercase tracking-widest text-brand-muted mb-1">
                Note interne
              </div>
              <textarea
                data-testid="contact-detail-notes"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                rows={3}
                className="w-full rounded-sm border border-brand-line px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-blue"
                placeholder="Aggiungi note interne (non visibili al richiedente)…"
              />
              <div className="flex justify-end mt-2">
                <button
                  onClick={saveNotes}
                  disabled={saving}
                  data-testid="contact-detail-save-notes"
                  className="text-sm text-brand-blue font-semibold hover:underline underline-offset-4 disabled:opacity-60"
                >
                  Salva note
                </button>
              </div>
            </div>

            <div className="flex flex-wrap gap-2 justify-between pt-3 border-t border-brand-line">
              <button
                onClick={() => setConfirmDelete(true)}
                data-testid="contact-detail-delete"
                className="inline-flex items-center gap-1.5 rounded-sm border border-red-300 text-red-700 hover:bg-red-50 px-3 py-2 text-sm font-semibold"
              >
                <Trash2 size={14} /> Elimina
              </button>
              <div className="flex gap-2">
                <button
                  onClick={() => setStatus("archived")}
                  disabled={saving || item.status === "archived"}
                  data-testid="contact-detail-archive"
                  className="inline-flex items-center gap-1.5 rounded-sm border border-brand-line text-brand-muted hover:border-brand-blue hover:text-brand-blue px-3 py-2 text-sm font-semibold disabled:opacity-50"
                >
                  <Archive size={14} /> Archivia
                </button>
                {item.status !== "new" ? (
                  <button
                    onClick={() => setStatus("new")}
                    disabled={saving}
                    data-testid="contact-detail-reopen"
                    className="inline-flex items-center gap-1.5 rounded-sm border border-brand-blue text-brand-blue hover:bg-brand-blue/5 px-3 py-2 text-sm font-semibold"
                  >
                    <RefreshCcw size={14} /> Riapri
                  </button>
                ) : (
                  <button
                    onClick={() => setStatus("handled")}
                    disabled={saving}
                    data-testid="contact-detail-handle"
                    className="btn-primary inline-flex items-center gap-1.5"
                  >
                    {saving ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                    Segna come gestita
                  </button>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={confirmDelete}
        onOpenChange={setConfirmDelete}
        title="Eliminare la richiesta?"
        description="L'operazione è definitiva."
        confirmLabel="Elimina"
        destructive
        onConfirm={doDelete}
        testid="confirm-delete-contact"
      />
    </>
  );
}

export default function AdminContactRequests() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);
  const [detailOpen, setDetailOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/admin/contact-requests", { params });
      setItems(data.items || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const open = (item) => {
    setSelected(item);
    setDetailOpen(true);
  };

  return (
    <div data-testid="admin-contact-requests" className="min-h-screen bg-brand-bg">
      <Topbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <button
          onClick={() => navigate("/admin")}
          data-testid="richieste-back-btn"
          className="inline-flex items-center gap-1.5 text-sm text-brand-blue hover:underline underline-offset-4 mb-6"
        >
          <ArrowLeft size={16} /> Torna alla dashboard
        </button>

        <div className="mb-6">
          <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-widest text-brand-gold mb-2">
            <Inbox size={14} /> Amministrazione
          </div>
          <h1 className="text-3xl sm:text-4xl text-brand-ink mb-2">
            Richieste di contatto
          </h1>
          <p className="text-brand-muted max-w-2xl">
            Elenco delle richieste ricevute dal form pubblico. Contatta il richiedente
            via email o telefono, poi segna la pratica come gestita.
          </p>
        </div>

        <div className="flex items-end justify-between flex-wrap gap-3 mb-6">
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-brand-muted" />
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger
                data-testid="richieste-filter-status"
                className="h-10 w-[180px] rounded-sm bg-white"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tutte</SelectItem>
                <SelectItem value="new">Nuove</SelectItem>
                <SelectItem value="handled">Gestite</SelectItem>
                <SelectItem value="archived">Archiviate</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <button
            onClick={load}
            disabled={loading}
            data-testid="richieste-refresh"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-brand-blue hover:underline underline-offset-4"
          >
            <RefreshCcw size={14} /> Aggiorna
          </button>
        </div>

        {loading ? (
          <div className="text-center py-16 text-brand-muted">
            <Loader2 size={18} className="animate-spin inline mr-2" /> Caricamento…
          </div>
        ) : items.length === 0 ? (
          <div
            data-testid="richieste-empty"
            className="bg-white border border-dashed border-brand-line rounded-sm p-14 text-center"
          >
            <Inbox size={32} className="mx-auto text-brand-blue/40 mb-3" />
            <div className="text-lg text-brand-ink mb-1">Nessuna richiesta</div>
            <p className="text-sm text-brand-muted">
              Le richieste inviate dal catalogo pubblico compariranno qui.
            </p>
          </div>
        ) : (
          <div className="bg-white border border-brand-line rounded-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-brand-bg border-b border-brand-line">
                <tr>
                  <th className="text-left p-3 text-[10px] uppercase tracking-widest text-brand-muted font-semibold">
                    Data
                  </th>
                  <th className="text-left p-3 text-[10px] uppercase tracking-widest text-brand-muted font-semibold">
                    Nome
                  </th>
                  <th className="text-left p-3 text-[10px] uppercase tracking-widest text-brand-muted font-semibold">
                    Contatti
                  </th>
                  <th className="text-left p-3 text-[10px] uppercase tracking-widest text-brand-muted font-semibold">
                    Corso
                  </th>
                  <th className="text-left p-3 text-[10px] uppercase tracking-widest text-brand-muted font-semibold">
                    Messaggio
                  </th>
                  <th className="text-left p-3 text-[10px] uppercase tracking-widest text-brand-muted font-semibold">
                    Stato
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr
                    key={it.id}
                    data-testid={`richiesta-row-${it.id}`}
                    onClick={() => open(it)}
                    className="border-b border-brand-line last:border-b-0 hover:bg-brand-bg cursor-pointer"
                  >
                    <td className="p-3 text-brand-muted whitespace-nowrap">
                      {formatDate(it.created_at)}
                    </td>
                    <td className="p-3 font-semibold text-brand-ink">{it.name}</td>
                    <td className="p-3 text-brand-muted">
                      <div className="inline-flex items-center gap-1 text-brand-blue">
                        <Mail size={11} /> {it.email}
                      </div>
                      {it.phone && (
                        <div className="inline-flex items-center gap-1 text-[11px] mt-0.5">
                          <Phone size={10} /> {it.phone}
                        </div>
                      )}
                    </td>
                    <td className="p-3 text-brand-ink">
                      {it.course_title_snapshot || (
                        <span className="text-brand-muted italic">generica</span>
                      )}
                    </td>
                    <td className="p-3 text-brand-muted max-w-[280px] truncate">
                      {it.message}
                    </td>
                    <td className="p-3">
                      <span
                        className={
                          "inline-block text-[10px] uppercase tracking-widest border rounded-sm px-2 py-0.5 " +
                          STATUS_BADGE[it.status]
                        }
                      >
                        {STATUS_LABEL[it.status]}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <DetailDialog
        item={selected}
        open={detailOpen}
        onOpenChange={setDetailOpen}
        onUpdated={(updated) => {
          setItems((prev) =>
            prev.map((x) => (x.id === updated.id ? updated : x))
          );
          setSelected(updated);
        }}
        onDeleted={(id) => setItems((prev) => prev.filter((x) => x.id !== id))}
      />
    </div>
  );
}
